import useDisclosure from '@hooks/useDisclosure';
import { ModalWrapper } from '@hooks/useModal';
import { Print } from '@mui/icons-material';
import {
  Badge,
  badgeClasses,
  IconButton,
  Stack,
  styled,
  Tooltip,
} from '@mui/material';
import { GridRowId } from '@mui/x-data-grid';
import { useState } from 'react';
import PrintQueueDatatable from './components/print-queue.datatable';
import useMenu from '@hooks/useMenu';
import {
  useDeletePrintQueueMutation,
  useGetPrintQueuesQuery,
} from '@api/pos/pos-print-queue.api.rtk';
import useAppContext from '@hooks/useAppContext';
import { ConfirmationDialog } from '@hooks/useConfirmationDialog';
import PrintQueueMenu from './components/print-queue.menu';
import { useAppSelector } from '@store/index';
import { selectPOSHasPrinter } from '@modules/point-of-sale/slice/pos-selector';

const PrintBadge = styled(Badge)`
  & .${badgeClasses.badge} {
    top: -14px;
    right: -6px;
    background-color: #ff5630;
    color: #fff;
  }
`;

const PrintQueue = () => {
  const { openSecCodeDialog, toast } = useAppContext();
  const menu = useMenu();

  const {
    removeSelectedData,
    selectedData,
    handleClose: handleCloseMenu,
  } = menu;

  const modalDisclosure = useDisclosure();
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const selectedRowsState = useState<GridRowId[]>([]);
  const [selectedRows, setSelectedRows] = selectedRowsState;
  const hasPrinter = useAppSelector(selectPOSHasPrinter);

  // Move useGetPrintQueuesQuery in PrintQueueDatatable when websocket is available
  const { data, isLoading, isFetching } = useGetPrintQueuesQuery(
    { isOpen: modalDisclosure.isOpen },
    { pollingInterval: 60000, skip: !hasPrinter },
  );
  // Move useGetPrintQueuesQuery in PrintQueueDatatable when websocket is available

  const [deletePrinter] = useDeletePrintQueueMutation();

  const handleDeletePrinter = async () => {
    await openSecCodeDialog(async (res) => {
      try {
        handleCloseMenu();

        if (!res?.data.isValid) return;
        const sourceArray =
          selectedRows?.length > 0 ? selectedRows : [selectedData];

        const promises = sourceArray.map((rowId) =>
          deletePrinter({
            printQueueID: rowId as string,
          }).unwrap(),
        );

        await Promise.all(promises);
      } catch (error) {
        toast({ message: error, severity: 'warning' });
      } finally {
        setIsOpenDialog(false);
      }
    }, 'void_sales'); // change priv code
  };

  const handleClose = () => {
    modalDisclosure.onClose();
    handleCloseMenu();
    setTimeout(() => {
      setSelectedRows([]);
      removeSelectedData();
    }, 100);
  };

  return (
    <>
      <Tooltip title={hasPrinter ? 'Print Queue' : 'No printer is configured.'}>
        <IconButton
          sx={{
            border: 0,
            ':hover': { bgcolor: 'unset' },
            bgcolor: 'transparent',
            cursor: !hasPrinter ? 'default' : 'pointer',
          }}
          onClick={!hasPrinter ? undefined : modalDisclosure.onOpen}
          disableRipple
        >
          <Print
            sx={{
              color: '#161C248F',
              fontSize: '24px',
              opacity: !hasPrinter ? 0.5 : 1,
            }}
          />
          <PrintBadge badgeContent={(data ?? []).length} overlap='circular' />
        </IconButton>
      </Tooltip>

      <ModalWrapper
        {...{
          modalDisclosure,
          withHeader: {
            title: 'Print Queue',
            onClose: handleClose,
          },
          boxStyle: { '.with-header-main': { pb: 1 } },
        }}
      >
        <Stack sx={{ flex: 1, minHeight: '500px', maxHeight: '600px' }}>
          <PrintQueueDatatable
            {...{
              menu,
              selectedRowsState,
              data: data ?? [],
              isLoading: isLoading || isFetching,
            }}
          />
        </Stack>
      </ModalWrapper>

      <PrintQueueMenu
        handleOpenDialog={() => setIsOpenDialog(true)}
        menu={menu}
      />

      <ConfirmationDialog
        open={isOpenDialog}
        onConfirm={handleDeletePrinter}
        onClose={() => setIsOpenDialog(false)}
      />
    </>
  );
};

export default PrintQueue;
